import React, { useState, useEffect } from "react";
import { useAuth } from "context/AuthContext";
import CardsHeader from "components/Headers/CardsHeader.js";
import PopulateRoutes from "./PopulateRoutes";
import userServices from "services/userServices";
import DropdownFilter from "../../../utils/DropdownFilter";
import helpers from "utils/helpers";
import { useTranslation } from "context/TranslationProvider";

import { Card, CardHeader, CardBody, Row, Col, Container } from "reactstrap";
import { Bar, Line } from "react-chartjs-2";

const Dashboard = () => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [avgPriceSenderListings, setAvgPriceSenderListings] = useState([]);
  const [avgPriceTravelerListings, setAvgPriceTravelerListings] = useState([]);
  const [searchLists, setSearchLists] = useState([]);
  const [priceFilter, setPriceFilter] = useState("7d");
  const [durationFilter, setDurationFilter] = useState("7d");
  const [priceType, setPriceType] = useState("average");
  const [userSignups, setUserSignups] = useState([]);
  const [totalUsers, setTotalUsers] = useState([]);
  const [cardStats, setCardStats] = useState({
    totalUsers: 0,
    totalListings: 0,
    totalOrders: 0,
    completedOrders: 0,
  });

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      try {
        await Promise.all([
          getPopularRoutes(),
          getDeliveryPricing(priceFilter),
          getAllUserSignups(durationFilter),
          getAllTotalUsers(durationFilter),
          getCardStats(),
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [priceFilter, durationFilter]);

  const getPopularRoutes = async () => {
    try {
      const res = await userServices.getPopularRoutes();
      const payload = res?.data?.data || res?.data || {};
      const { senderRoutes = [], travelerRoutes = [] } = payload;
      setSearchLists([...senderRoutes, ...travelerRoutes]);
    } catch (err) {
      console.error("Popular Routes error:", err);
    }
  };

  const getDeliveryPricing = async (filter) => {
    try {
      const res = await userServices.getDeliveryPricing(filter);
      const payload = res?.data?.data || res?.data || {};
      const { senderAvg, travelerAvg } = payload;
      setAvgPriceSenderListings(senderAvg?.breakdown || []);
      setAvgPriceTravelerListings(travelerAvg?.breakdown || []);
    } catch (err) {
      console.error("Delivery pricing error:", err);
    }
  };

  const getAllUserSignups = async (filter) => {
    try {
      const res = await userServices.getUserSignups(filter);
      const payload = res?.data?.data || res?.data || {};
      const resultData = payload.result || (Array.isArray(payload) ? payload : []);
      setUserSignups(resultData);
    } catch (err) {
      console.error("User signups error:", err);
    }
  };

  const getAllTotalUsers = async (filter) => {
    try {
      const res = await userServices.getTotalUsersRegistration(filter);
      const payload = res?.data?.data || res?.data || {};
      const resultData = payload.result || (Array.isArray(payload) ? payload : []);
      setTotalUsers(resultData);
    } catch (err) {
      console.error("Total users error:", err);
    }
  };

  const getCardStats = async () => {
    try {
      const res = await userServices.getUserStats();
      const payload = res?.data?.data || res?.data || {};
      const stats = payload.result || payload;
      setCardStats({
        totalUsers: stats?.totalUsers || 0,
        totalListings: stats?.totalListings || 0,
        totalOrders: stats?.totalOrders || 0,
        completedOrders: stats?.completedOrders || 0,
      });
    } catch (error) {
      console.error("Card stats error:", error);
    }
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          autoSkip: true,
        },
      },
      y: {
        beginAtZero: true,
      },
    },
    plugins: {
      legend: { display: true },
    },
  };

  const chartData = (label, data, isSender) => ({
    labels: data.map((item) => item.period),
    datasets: [
      {
        label,
        data: data.map((item) => item[priceType]),
        maxBarThickness: 10,
        backgroundColor: isSender ? "#f5365c" : "#5e72e4",
      },
    ],
  });

  const userSignupChart = {
    data: {
      labels: userSignups.map((item) => item._id),
      datasets: [
        {
          label: t("analytics.dashboard.dailySignups", "Daily Signups"),
          data: userSignups.map((item) => item.count),
          maxBarThickness: 10,
          backgroundColor: "#5e72e4",
        },
      ],
    },
    options: barChartOptions,
  };

  const totalUsersChart = {
    data: {
      labels: totalUsers.map((item) => item.period),
      datasets: [
        {
          label: t("analytics.dashboard.overallSignups", "Overall Signups"),
          data: totalUsers.map((item) => item.totalUsers),
          borderColor: "#f5365c",
          tension: 0.4,
          fill: false,
        },
      ],
    },
    options: barChartOptions,
  };

  return (
    <main>
      <CardsHeader
        name={t("analytics.dashboard.title", "Dashboard")}
        parentName={t("analytics.dashboard.title", "Dashboard")}
        totalUsers={cardStats.totalUsers}
        totalOrders={cardStats.totalOrders}
        totalListings={cardStats.totalListings}
        completedOrders={cardStats.completedOrders}
        routes={{
          totalUsers: "/admin/users",
          totalOrders: "/admin/all-orders",
          totalListings: "/admin/all-listings",
          completedOrders: "/admin/all-orders",
        }}
      />

      <Container fluid className={isAuthenticated ? "w-100" : "w-75"}>
        <PopulateRoutes items={searchLists} isItemsLoading={loading} />

        <Card className="px-4 pt-4 mb-4">
          <div className="d-flex flex-column flex-sm-row justify-content-sm-end align-items-sm-center mb-3 gap-2">
            <div>
              <DropdownFilter
                label={t("analytics.dashboard.priceDuration", "Price Duration")}
                value={priceFilter}
                options={helpers.getFilter("TIME", t)}
                onChange={setPriceFilter}
              />
            </div>
            <div className="mt-2 mt-sm-0 ml-sm-2">
              <DropdownFilter
                label={t("analytics.dashboard.priceType", "Price Type")}
                value={priceType}
                options={helpers.getFilter("PRICE_TYPE", t)}
                onChange={setPriceType}
                capitalize
              />
            </div>

          </div>

          <Row>
            <Col xl="6">
              <Card className="shadow">
                <CardHeader>
                  <h6 className="surtitle">
                    {t("analytics.dashboard.charts.senderPerformance", "Sender Performance")}
                  </h6>
                  <h5 className="h3 mb-0">
                    {t("analytics.dashboard.charts.deliveryPricing", "Delivery Pricing")}
                  </h5>
                </CardHeader>
                <CardBody>
                  <div className="chart">
                    <Bar
                      data={chartData(
                        `${t("analytics.dashboard.charts.senderPrice", "Sender Price")} (${t(
                          `analytics.dashboard.type.${priceType}`
                        )})`,
                        avgPriceSenderListings,
                        true
                      )}
                      options={barChartOptions}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl="6">
              <Card className="shadow">
                <CardHeader>
                  <h6 className="surtitle">
                    {t("analytics.dashboard.charts.travelerPerformance", "Traveler Performance")}
                  </h6>
                  <h5 className="h3 mb-0">
                    {t("analytics.dashboard.charts.deliveryPricing", "Delivery Pricing")}
                  </h5>
                </CardHeader>
                <CardBody>
                  <div className="chart">
                    <Bar
                      data={chartData(
                        `${t("analytics.dashboard.charts.travelerPrice", "Traveler Price")} (${t(
                          `analytics.dashboard.type.${priceType}`
                        )})`,
                        avgPriceTravelerListings,
                        false
                      )}
                      options={barChartOptions}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Card>

        <Card className="px-4 pt-4">
          <div className="d-flex justify-content-end mb-3">
            <DropdownFilter
              label={t("analytics.dashboard.priceDuration", "Price Duration")}
              value={durationFilter}
              options={helpers.getFilter("TIME", t)}
              onChange={setDurationFilter}
            />
          </div>

          <Row>
            <Col xl="6">
              <Card className="shadow">
                <CardHeader>
                  <h6 className="surtitle">
                    {t("analytics.dashboard.charts.newUser", "New User Signups Performance")}
                  </h6>
                  <h5 className="h3 mb-0">
                    {t("analytics.dashboard.newUsersSignups", "New User Signups")}
                  </h5>
                </CardHeader>
                <CardBody>
                  <div className="chart">
                    <Bar {...userSignupChart} />
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl="6">
              <Card className="shadow">
                <CardHeader>
                  <h6 className="surtitle">
                    {t("analytics.dashboard.charts.totalUserPerformance", "Total User Signups Performance")}
                  </h6>
                  <h5 className="h3 mb-0">
                    {t("analytics.dashboard.totalUserGrowth", "Total User Growth")}
                  </h5>
                </CardHeader>
                <CardBody>
                  <div className="chart">
                    <Line {...totalUsersChart} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Card>
      </Container>
    </main>
  );
};

export default Dashboard;
